import { createFileRoute, retainSearchParams, useParams, useNavigate, Link, useMatch } from '@tanstack/react-router'
import { trpc } from '@/main'
import { useSessionUser } from '@/hooks/use-session'
import { noop, useSuspenseQuery, useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { Suspense, useState, useDeferredValue, type Ref } from 'react'
import { toast } from '@/lib/toast'
import { PlusIcon, FileTextIcon } from 'lucide-react'
import type { RootSearch } from '@/routes/__root'
import { useLingui } from '@lingui/react/macro'
import { useSearchParamDialog } from '@/hooks/use-search-param-dialog'
import { usePermissions } from '@/hooks/use-permissions'
import { clearProfileSelection, useRootSearch } from '@/hooks/use-root-search'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { useDebouncedSearch } from '@/hooks/use-debounced-search'
import { Button, ButtonLink } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { Command, CommandDialog, CommandInput, CommandList, CommandEmpty, CommandItem } from '@/components/ui/command'
import { SectionLabel } from '@/components/custom/section-label'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { ReportPager } from './-components/report/report-pager'
import { usePreviewMode } from '@/components/custom/route-transition/surface/route-preview'
import { NavStackStage, NavStackScreen, useStackedLayout } from '@/components/custom/route-transition/stack/nav-stack'
import { SectionStack } from './-components/shared/section-stack'
import { ReportScreenTitle } from './-components/report/report-screen-title'
import { ListPanelBodySkeleton, MembersPanelSkeleton } from './-components/app-layout-skeleton'
import { ListPanePending } from './-components/shared/list-pane-pending'
import {
  PageLayout,
  StickyPanel,
  StickyHeader,
  ScrollableContent,
  SlideInPanel,
  CollapsibleListPanel,
  CollapsibleListPanelBorder,
} from './-components/shared/layout'
import {
  CollapsiblePanelProvider,
  CollapsiblePanel,
  CollapsiblePanelContent,
} from './-components/shared/collapsible-panel'
import { ScrollableList } from './-components/shared/scrollable-list'
import { SelectionHint, CollapsibleSectionLabel, CollapsibleToolbar } from './-components/shared/collapsible'
import { Avatar } from './-components/shared/avatar'
import { PanelEmptyState } from './-components/shared/panel-empty-state'
import { JobTreeItem } from './-components/jobs/job-tree-item'
import { JobsEmployeesPanel } from './-components/jobs/jobs-employees-panel'
import { JobActionMenu } from './-components/jobs/job-action-menu'
import { ReportTabs } from './-components/report/report-tabs'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
// Dialogs — only mount when triggered by user (search-param or selection state).
import { JobDialog } from './-components/jobs/job-dialog'
import { DescribeProfileDialog } from './-components/offers/describe-profile-dialog'
import { EmployeeForm } from './-components/companies/employee-form'

const CRUMB_LINK_CLASS = 'text-foreground hover:text-foreground/70 text-base font-semibold sm:text-xl'

export const Route = createFileRoute('/_app/jobs')({
  search: {
    middlewares: [retainSearchParams<RootSearch>(true)],
  },
  loaderDeps: ({ search }) => ({ q: search.q }),
  // The rail list's key. `q` lives in the URL (the search box writes it), so the loader and the panel
  // read the ONE object built here — there is no unsearched-vs-searched pair to hold in sync. Puestos
  // are always positions (`isPosition: true`).
  context: ({ deps }) => ({
    jobsListInfiniteQueryOptions: trpc.jobs.listJobs.infiniteQueryOptions(
      { q: deps.q, isPosition: true },
      { getNextPageParam: (lastPage) => lastPage.nextCursor },
    ),
  }),
  loader: ({ context }) => {
    context.queryClient.infiniteQuery(context.jobsListInfiniteQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: ListPanePending,
})

function RouteComponent() {
  const { t } = useLingui()
  const user = useSessionUser()
  // Company-view (Block C): an admin/authorized employee in company view gets the company Jobs interface
  // (not their own assigned Position), so they're treated as not-an-employee-view here.
  const isCompanyView = usePermissions().isCompanyView
  const isEmployee = user?.type === 'applicant' && user.isEmployee && !isCompanyView

  // Access (logged-out → /login; self-registered professional → /report) is enforced centrally by the
  // `_app` layout guard (see route-access), before this route's gated `listJobs` query ever mounts.

  // Inside a report-tab live preview (/jobs/<id>/board …), strip the list/report split to just the pager pane.
  if (usePreviewMode('/jobs') === 'passthrough') return <ReportPager className="h-full" />

  return (
    <div id="report" className="@container flex h-full flex-col">
      {/* On a phone the three panes below are STACKED SCREENS, not a squashed row: the jobs list, the job's
          integrants, and the report, each pushed in from the right over the last (see nav-stack.tsx).
          The provider sits above PageLayout so the title bar can carry the stack's back chevron. */}
      <JobsStack>
        <PageLayout
          icon={NAVIGATION_ICONS.jobs}
          title={isEmployee ? t`Position` : t`Jobs`}
          breadcrumbsSlot={<JobsBreadcrumbs />}
          titleActions={<JobsTitleActions />}
        >
          <JobsLayout sectionTitle={isEmployee ? t`Position` : t`Jobs`} />
        </PageLayout>
      </JobsStack>
    </div>
  )
}

/**
 * Jobs' mobile hierarchy — list → the job's integrants → its report. The standard section shape, so the whole
 * declaration is the three routes involved; `SectionStack` owns the depth derivation and the pop policy.
 */
function JobsStack(props: { children: React.ReactNode }) {
  const { jobId } = useParams({ strict: false })
  return (
    <SectionStack list={{ to: '/jobs' }} detail={jobId ? { to: '/jobs/$jobId', params: { jobId } } : null}>
      {props.children}
    </SectionStack>
  )
}

function JobsLayout(props: { sectionTitle: string }) {
  const { t } = useLingui()
  const { jobId } = useParams({ strict: false })
  const navigate = useNavigate()
  const [describeDialogOpen, setDescribeDialogOpen] = useState(false)
  const [dialogJobId, setDialogJobId] = useState<number | null>(null)
  // Job ids that should navigate to the report on describe-dialog close.
  const [postCreateNavigateJobs, setPostCreateNavigateJobs] = useState(new Set<number>())
  const jobDialog = useSearchParamDialog('editJob')
  const editJobIdFromSearch = typeof jobDialog.value === 'number' ? jobDialog.value : undefined

  // Employee edit dialog (triggered from position conflict dialog)
  const employeeDialog = useSearchParamDialog('editEmployee')
  const editEmployeeId = typeof employeeDialog.value === 'number' ? employeeDialog.value : undefined

  // `?describeJob=<id>` — the URL-addressable way into the describe wizard, and the only one. Every
  // in-app entry point (the post-create chain below, the row action menus) opens it from local state,
  // so before this the public API's "link to describe the job" (`GET /api/v1/jobs/:id/test`) had no
  // real destination. Kept separate from `dialogJobId` because that one carries the post-create
  // navigate-to-report behaviour, which a deep link must NOT inherit — closing a deep-linked wizard
  // leaves you where you are.
  const describeDialog = useSearchParamDialog('describeJob')
  const describeJobIdFromSearch = typeof describeDialog.value === 'number' ? describeDialog.value : undefined

  const handleDialogClose = (open: boolean) => {
    setDescribeDialogOpen(open)
    if (!open && dialogJobId) {
      const closedJobId = dialogJobId
      // Clear dialogJobId after a small delay to allow dialog to close gracefully
      setTimeout(() => setDialogJobId(null), 300)
      if (postCreateNavigateJobs.has(closedJobId)) {
        setPostCreateNavigateJobs((prev) => {
          const next = new Set(prev)
          next.delete(closedJobId)
          return next
        })
        toast.success(t`Saved successfully`)
        navigate({
          to: '/jobs/$jobId/report',
          params: { jobId: closedJobId.toString() },
        })
      }
    }
  }

  const hasJobSelected = !!jobId

  // No viewport-driven auto-collapse: the nav stack gives each pane the WHOLE screen and decides which one is on
  // it, so both providers stay expanded and the collapse is desktop's business only.
  return (
    <CollapsiblePanelProvider defaultCollapsed={false}>
      <Suspense fallback={null}>
        {employeeDialog.mounted && (
          <EmployeeForm
            employeeId={editEmployeeId}
            open={employeeDialog.open}
            onOpenChange={employeeDialog.onOpenChange}
          />
        )}
        {dialogJobId != null && (
          <DescribeProfileDialog jobId={dialogJobId} open={describeDialogOpen} onOpenChange={handleDialogClose} />
        )}
        {/* Mutually exclusive with the local-state instance above, so only ever one wizard is mounted. */}
        {dialogJobId == null && describeDialog.mounted && describeJobIdFromSearch !== undefined && (
          <DescribeProfileDialog
            jobId={describeJobIdFromSearch}
            open={describeDialog.open}
            onOpenChange={describeDialog.onOpenChange}
          />
        )}
        {jobDialog.mounted && (
          <JobDialog
            jobId={editJobIdFromSearch}
            open={jobDialog.open}
            onOpenChange={jobDialog.onOpenChange}
            onCreated={(newJobId) => {
              setPostCreateNavigateJobs((prev) => new Set(prev).add(newJobId))
              setDialogJobId(newJobId)
              setDescribeDialogOpen(true)
            }}
          />
        )}
      </Suspense>
      {/* The panel row on desktop, the stack's stage on mobile — same div, same classes, see
          NavStackStage. Each pane is wrapped in the level it occupies in the mobile hierarchy; on
          desktop those wrappers are `display: contents` and this lays out exactly as it always did. */}
      <NavStackStage className="flex w-full overflow-hidden" style={{ containerType: 'inline-size' }}>
        {/* LEVEL 0 — Jobs list (always visible on desktop, squashes to a 52px icon column when a job is
            selected; content keeps its full width inside and is clipped so only the avatars show) */}
        <NavStackScreen index={0} title={props.sectionTitle}>
          <CollapsibleListPanel isOpen={hasJobSelected} className="print:hidden">
            <CollapsibleListPanelBorder>
              <CollapsiblePanel expandedWidth="100%" disableTransition>
                {/* Single-list panel: the list view inside `CollapsiblePanelContent` is a `@container`
                    child of `SquashPanel`, so the same rendered list responsively collapses into a
                    centered avatar column (icon rail) at narrow widths via container queries. No dual
                    rendering — `JobsPanelContent` drives both states. */}
                <CollapsiblePanelContent>
                  <Suspense fallback={<ListPanelBodySkeleton />}>
                    <JobsPanelContent variant="sidebar" />
                  </Suspense>
                </CollapsiblePanelContent>
              </CollapsiblePanel>
            </CollapsibleListPanelBorder>
          </CollapsibleListPanel>
        </NavStackScreen>

        {/* Right Panel Group (Middle + Detail) - width animates from 0 to target. `ns-passthrough`: on
            mobile it stops being a box so the two levels inside position against the stage, not against
            this group's (0px / clipped) width. */}
        <SlideInPanel isOpen={hasJobSelected} innerClassName="flex" className="ns-passthrough">
          {/* LEVEL 1 — the job's integrants. The first screen a job opens onto on mobile. */}
          <NavStackScreen
            index={1}
            title={<JobScreenTitle />}
            actions={jobId ? <JobScreenActions jobId={jobId} /> : null}
          >
            <CollapsiblePanelProvider cookieName="panel_state_middle" keyboardShortcut="]" defaultCollapsed={false}>
              <StickyPanel className="print:hidden">
                <CollapsiblePanel>
                  <Suspense fallback={<MembersPanelSkeleton />}>
                    <JobsEmployeesPanel />
                  </Suspense>
                </CollapsiblePanel>
              </StickyPanel>
            </CollapsiblePanelProvider>
          </NavStackScreen>

          {/* LEVEL 2 — the report. The `jobs.$jobId` layout route renders here via <AnimatedOutlet/>. It
              lives HERE (not in the `$jobId` layout) so the enter animation plays once when the report panel
              first appears and never re-animates on tab/job switches — see AnimatedOutlet. */}
          <NavStackScreen index={2} title={<ReportScreenTitle />}>
            <ScrollableContent>
              <StickyHeader>
                <ReportTabs />
              </StickyHeader>
              {/* No <ScrollableArea/> wrapper: the pager puts one inside each pane (see ReportPager). */}
              {hasJobSelected && <ReportPager className="min-h-0 flex-1" />}
            </ScrollableContent>
          </NavStackScreen>
        </SlideInPanel>
      </NavStackStage>
    </CollapsiblePanelProvider>
  )
}

function JobsBreadcrumbs() {
  const { t } = useLingui()
  const user = useSessionUser()
  const isCompanyView = usePermissions().isCompanyView
  const isEmployee = user?.type === 'applicant' && user.isEmployee && !isCompanyView
  const { jobId } = useParams({ strict: false })
  const sectionLabel = isEmployee ? t`Position` : t`Jobs`
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink
            className={CRUMB_LINK_CLASS}
            render={
              <Link
                to="/jobs"
                search={(prev) => ({
                  ...clearProfileSelection(prev),
                })}
              >
                {sectionLabel}
              </Link>
            }
          />
        </BreadcrumbItem>
        {jobId && (
          <Suspense fallback={null}>
            <JobDetailCrumb jobId={jobId} />
          </Suspense>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

function JobDetailCrumb(props: { jobId: string }) {
  // This renders in the LIST layout, above the `$jobId` match, so it has no context of its own for
  // this entity — it reads the OWNING layout's, matched exactly when the id is present, which is the
  // guard every call site already applies.
  const { context } = useMatch({ from: '/_app/jobs/$jobId' })
  const { data: job } = useSuspenseQuery(context.jobQueryOptions)
  if (!job?.name) return null
  return (
    <>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbLink
          className={CRUMB_LINK_CLASS}
          render={
            <Link
              to="/jobs/$jobId/report"
              params={{ jobId: props.jobId }}
              search={(prev) => ({
                ...clearProfileSelection(prev),
              })}
            >
              {job.name}
            </Link>
          }
        />
      </BreadcrumbItem>
    </>
  )
}

function JobsTitleActions() {
  const { jobId } = useParams({ strict: false })
  if (!jobId) return null
  return (
    <Suspense fallback={null}>
      <JobsTitleActionsLoaded />
    </Suspense>
  )
}

/**
 * The title of the JOB screen (nav stack level 1) — the job's own name, the same thing the desktop
 * breadcrumb's second crumb shows. Suspends on `getJob`, which the `$jobId` layout has already prefetched, so
 * it fills in with the rest of the screen; the fallback keeps the bar's height either way.
 */
function JobScreenTitle() {
  const { jobId } = useParams({ strict: false })
  if (!jobId) return null
  return (
    <Suspense fallback={null}>
      <JobName />
    </Suspense>
  )
}

function JobName() {
  // This renders in the LIST layout, above the `$jobId` match, so it has no context of its own for
  // this entity — it reads the OWNING layout's, matched exactly when the id is present, which is the
  // guard every call site already applies.
  const { context } = useMatch({ from: '/_app/jobs/$jobId' })
  const { data: job } = useSuspenseQuery(context.jobQueryOptions)
  return <>{job?.name ?? ''}</>
}

/**
 * The JOB screen's header actions: the job's own report (one push deeper, the same level a tapped integrant
 * lands on but with the job selected instead of a person — on desktop that pane is always beside the list, so
 * there is nothing to reach for there) and the owner's action menu.
 */
function JobScreenActions(props: { jobId: string }) {
  const { t } = useLingui()
  return (
    <>
      <ButtonLink
        variant="ghost"
        size="icon"
        aria-label={t`Job report`}
        className="size-8"
        to="/jobs/$jobId/report"
        params={{ jobId: props.jobId }}
        search={(prev) => ({ ...prev, second: undefined, selected: 'first' })}
      >
        <FileTextIcon className="size-4" />
      </ButtonLink>
      <Suspense fallback={null}>
        <JobsTitleActionsLoaded />
      </Suspense>
    </>
  )
}

function JobsTitleActionsLoaded() {
  // This renders in the LIST layout, above the `$jobId` match, so it has no context of its own for
  // this entity — it reads the OWNING layout's, matched exactly when the id is present, which is the
  // guard every call site already applies.
  const { context } = useMatch({ from: '/_app/jobs/$jobId' })
  const { data: job } = useSuspenseQuery(context.jobQueryOptions)
  return job.viewerRole === 'owner' ? <JobActionMenu job={job} variant="header" /> : null
}

type PanelJob = {
  id: number
  name: string | null
  custom: { competences: boolean; values: boolean; personality: boolean; all: boolean }
}

// Reads the loader-prefetched `listJobs` infinite query (see this route's `loader`) and feeds the
// rail list view.
function JobsPanelContent(props: { variant?: 'sidebar' | 'main' }) {
  const user = useSessionUser()
  const isCompanyView = usePermissions().isCompanyView
  const isEmployee = user?.type === 'applicant' && user.isEmployee && !isCompanyView
  const { q: searchQueryFromUrl } = useRootSearch()
  const { searchInput, handleSearchChange } = useDebouncedSearch({ initialValue: searchQueryFromUrl })
  // The `q` that produced the list currently on screen — deferred in lockstep with the options below.
  const deferredQuery = useDeferredValue(searchQueryFromUrl)

  // Deferring the route's OPTIONS object keeps the PREVIOUS list on screen while a new search page loads
  // (keep-previous UX); the staleness drives the search/progress spinner via `isFetching`.
  const context = Route.useRouteContext()
  const deferredJobs = useDeferredValue(context.jobsListInfiniteQueryOptions)
  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery(deferredJobs)
  // True while the deferred options lag the live ones (a change is in flight) — keeps the search spinner /
  // fetching bar visible during the keep-previous transition.
  const isJobsFetching = isFetching || deferredJobs !== context.jobsListInfiniteQueryOptions

  const { loadMoreRef } = useInfiniteScroll({
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage,
  })

  const jobs = data?.pages.flatMap((page) => page.list) ?? []
  const totalCount = data?.pages[0]?.pagination.count ?? 0

  return (
    <JobsPanelView
      variant={props.variant}
      jobs={jobs}
      totalCount={totalCount}
      isEmployee={isEmployee}
      isFetching={isJobsFetching}
      searchInput={searchInput}
      onSearchChange={handleSearchChange}
      loadMoreRef={loadMoreRef}
      isFetchingMore={isFetchingNextPage}
      // The search term that produced the data currently on screen (the DEFERRED `q`, not the live
      // URL one). Empty-state decisions must key off this so clearing the search doesn't briefly flash
      // the cold "no items yet" state (live `q` is already '' while the stale search list is still shown).
      activeQuery={deferredQuery}
    />
  )
}

function JobsPanelView(props: {
  variant?: 'sidebar' | 'main'
  jobs: PanelJob[]
  totalCount: number
  isEmployee: boolean
  isFetching: boolean
  searchInput: string
  onSearchChange: (value: string) => void
  loadMoreRef?: Ref<HTMLDivElement>
  isFetchingMore?: boolean
  activeQuery: string
}) {
  const { t } = useLingui()
  const isEmployee = props.isEmployee
  const allJobs = props.jobs
  const totalCount = props.totalCount
  const activeQuery = props.activeQuery
  const { jobId } = useParams({ strict: false })
  const { q: searchQueryFromUrl } = useRootSearch()
  const navigate = useNavigate()
  const [searchOpen, setSearchOpen] = useState(false)
  // In a stack this list is a whole level, and a pop always lands on it with the selection cleared — so its
  // hint must be on screen ALREADY while the level above is dragged off it, not appear on the commit and
  // shove the list down a row at the end of the gesture. (The rows' own selected highlight is suppressed
  // inside JobTreeItem — see useStackedSelection.)
  const stacked = useStackedLayout()

  const handleCreateJob = () => {
    navigate({ to: '.', search: (prev) => ({ ...prev, editJob: 'new' }) })
  }
  // Create is owner-only (company account, or admin in company view); an `authorized` employee viewing
  // the company is read-only (the backend also rejects the write — Block A/C).
  const canCreateJobs = usePermissions().can('jobs:write')

  const showOnlyEmptyState = totalCount === 0 && !activeQuery

  // Employees have a single assigned position; companies pick from a list — different selection hint.
  const selectionHintText = isEmployee
    ? t`Click on the position to view its report`
    : t`Select a job to view its members and reports`

  const isMainVariant = props.variant === 'main'

  if (isMainVariant) {
    return (
      <div className="flex h-full flex-col">
        {/* Employees only ever have a single assigned position, so the search box is hidden for them. */}
        {!showOnlyEmptyState && !isEmployee && (
          <>
            <div className="flex items-center gap-2 border-b p-3">
              <SearchInput
                placeholder={t`Search jobs...`}
                value={props.searchInput}
                onChange={props.onSearchChange}
                isLoading={props.isFetching && searchQueryFromUrl.length > 0}
              />
              {!isEmployee && canCreateJobs && (
                <Button variant="secondary" onClick={handleCreateJob}>
                  <PlusIcon className="size-4" />
                  {t`Add`}
                </Button>
              )}
            </div>
          </>
        )}

        <ScrollableList
          isFetching={props.isFetching}
          loadMoreRef={props.loadMoreRef}
          isFetchingMore={props.isFetchingMore}
          isEmpty={allJobs.length === 0}
          emptyState={
            <PanelEmptyState
              noResults={allJobs.length === 0 && !!activeQuery}
              icon={<NAVIGATION_ICONS.jobs className="text-muted-foreground size-5" />}
              title={isEmployee ? t`No assigned position yet` : t`No jobs yet`}
              description={isEmployee ? t`Your company has not assigned you a position` : t`Add jobs to get started`}
              action={
                !isEmployee && canCreateJobs ? (
                  <Button variant="secondary" onClick={handleCreateJob}>
                    <PlusIcon className="size-4" />
                    {t`Add job`}
                  </Button>
                ) : undefined
              }
            />
          }
          hint={allJobs.length > 0 && <SelectionHint visible={stacked || !jobId} text={selectionHintText} />}
        >
          <SectionLabel className="px-2">
            {totalCount === 1 ? t`${totalCount} job` : t`${totalCount} jobs`}
          </SectionLabel>
          {allJobs.map((job) => (
            <JobTreeItem key={job.id} job={job} isSelected={jobId === job.id.toString()} hideMenu={isEmployee} />
          ))}
        </ScrollableList>
      </div>
    )
  }

  return (
    <>
      {/* Employees only ever have a single assigned position, so the search box is hidden for them. */}
      {!showOnlyEmptyState && !isEmployee && (
        <>
          <CollapsibleToolbar>
            <CollapsibleToolbar.Search onSearchClick={() => setSearchOpen(true)} searchLabel={t`Search jobs`}>
              <SearchInput
                placeholder={t`Search jobs...`}
                value={props.searchInput}
                onChange={props.onSearchChange}
                isLoading={props.isFetching && searchQueryFromUrl.length > 0}
              />
            </CollapsibleToolbar.Search>
            {!isEmployee && canCreateJobs && (
              <CollapsibleToolbar.Action
                icon={<PlusIcon className="size-4" />}
                label={t`Add`}
                tooltip={t`Add job`}
                onClick={handleCreateJob}
              />
            )}
          </CollapsibleToolbar>
        </>
      )}

      <ScrollableList
        isFetching={props.isFetching}
        loadMoreRef={props.loadMoreRef}
        isFetchingMore={props.isFetchingMore}
        isEmpty={allJobs.length === 0}
        emptyState={
          <PanelEmptyState
            noResults={allJobs.length === 0 && !!activeQuery}
            icon={<NAVIGATION_ICONS.jobs className="text-muted-foreground size-5" />}
            title={isEmployee ? t`No assigned position yet` : t`No jobs yet`}
            description={isEmployee ? t`Your company has not assigned you a position` : t`Add jobs to get started`}
            action={
              !isEmployee && canCreateJobs ? (
                <Button variant="secondary" onClick={handleCreateJob}>
                  <PlusIcon className="size-4" />
                  {t`Add job`}
                </Button>
              ) : undefined
            }
          />
        }
        hint={allJobs.length > 0 && <SelectionHint visible={stacked || !jobId} text={selectionHintText} />}
      >
        <CollapsibleSectionLabel>
          {totalCount === 1 ? t`${totalCount} job` : t`${totalCount} jobs`}
        </CollapsibleSectionLabel>
        {allJobs.map((job) => (
          <JobTreeItem key={job.id} job={job} isSelected={jobId === job.id.toString()} hideMenu={isEmployee} />
        ))}
      </ScrollableList>

      <CommandDialog open={searchOpen} onOpenChange={setSearchOpen} title={t`Search jobs`}>
        <Command className="gap-1">
          <CommandInput placeholder={t`Search jobs...`} />
          <CommandList>
            <CommandEmpty>{t`No jobs found`}</CommandEmpty>
            {allJobs.map((job) => (
              <CommandItem
                key={job.id}
                value={`${job.name ?? ''} ${job.id}`}
                onSelect={() => {
                  navigate({
                    to: '/jobs/$jobId/report',
                    params: { jobId: job.id.toString() },
                    search: (prev) => ({
                      ...clearProfileSelection(prev),
                      first: { type: 'job', id: job.id },
                      selected: 'first',
                    }),
                  })
                  setSearchOpen(false)
                }}
              >
                <Avatar size="sm" type="job" name={job.name} />
                <span className="truncate">{job.name}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  )
}
